/*
 * Purpose: Tenant-scoped MCP server registry (metadata; secrets in vault)
 * Last modified: 2026-06-28 | Agent
 * Completeness: 92
 */
import { create } from 'zustand';
import { migrateLegacyTenantStorageIfNeeded, tenantStorageKey } from '@/lib/tenantStorage';
import type { McpServerRecord } from '@/services/connectors/mcp/types';
import {
  slugifyMcpId,
  urlSecretForMcpId,
  tokenSecretForMcpId,
} from '@/services/connectors/mcp/mcpConnection';

function mcpStorageKey(): string {
  return tenantStorageKey('mcp_servers');
}

function loadServers(): McpServerRecord[] {
  try {
    migrateLegacyTenantStorageIfNeeded();
    const raw = localStorage.getItem(mcpStorageKey());
    if (!raw) return [];
    return JSON.parse(raw) as McpServerRecord[];
  } catch {
    return [];
  }
}

function persistServers(servers: McpServerRecord[]) {
  migrateLegacyTenantStorageIfNeeded();
  localStorage.setItem(mcpStorageKey(), JSON.stringify(servers));
}

interface McpStore {
  servers: McpServerRecord[];
  load: () => void;
  getById: (id: string) => McpServerRecord | undefined;
  upsert: (input: { name: string; id?: string }) => McpServerRecord;
  markConnected: (
    id: string,
    patch: Pick<McpServerRecord, 'serverInfo' | 'toolNames'> & { connectedAt: string },
  ) => void;
  markError: (id: string, message: string) => void;
  remove: (id: string) => void;
}

export const useMcpStore = create<McpStore>((set, get) => ({
  servers: loadServers(),

  load: () => set({ servers: loadServers() }),

  getById: (id) => get().servers.find((s) => s.id === id),

  upsert: ({ name, id: preferredId }) => {
    const trimmed = name.trim();
    const id = preferredId ?? slugifyMcpId(trimmed);
    const existing = get().getById(id);
    const record: McpServerRecord = {
      id,
      name: trimmed,
      urlSecret: existing?.urlSecret ?? urlSecretForMcpId(id),
      tokenSecret: existing?.tokenSecret ?? tokenSecretForMcpId(id),
      transport: 'streamable-http',
      connectedAt: existing?.connectedAt,
      toolNames: existing?.toolNames,
      serverInfo: existing?.serverInfo,
    };
    set((state) => {
      const filtered = state.servers.filter((s) => s.id !== id);
      const servers = [...filtered, record];
      persistServers(servers);
      return { servers };
    });
    return record;
  },

  markConnected: (id, patch) => {
    set((state) => {
      const servers = state.servers.map((s) =>
        s.id === id
          ? {
              ...s,
              ...patch,
              lastError: undefined,
            }
          : s,
      );
      persistServers(servers);
      return { servers };
    });
  },

  markError: (id, message) => {
    set((state) => {
      const servers = state.servers.map((s) =>
        s.id === id ? { ...s, lastError: message, connectedAt: undefined } : s,
      );
      persistServers(servers);
      return { servers };
    });
  },

  remove: (id) => {
    set((state) => {
      const servers = state.servers.filter((s) => s.id !== id);
      persistServers(servers);
      return { servers };
    });
  },
}));
